import { clsx } from 'clsx'

import { CaptionsIcon, ImageIcon } from '@buffer-mono/popcorn'

import { SERVICE_TIKTOK } from '~publish/legacy/constants'
import { parseTikTokLinks } from '~publish/legacy/utils/channels/tiktok/helpers'
import { parseTextToElements } from '~publish/legacy/utils/parseTextToElements'

import { TikTokAvatar } from './TikTokAvatar'
import {
  HeartIcon,
  CommentIcon,
  BookmarkIcon,
  ShareIcon,
  MusicIcon,
  SearchIcon,
} from '../../../../assets/tiktok'
import {
  EmptyMediaPlaceholder,
  PostWrapper as PostWrapperCommon,
  PreviewAiMarker,
} from '../../../Common/styles'
import {
  DEFAULT_AVATAR,
  getMediaProperties,
  getOrientation,
  getPreviewCharLimit,
  ImageOrientation,
  textExceedsCharLimit,
  truncateText,
} from '../../../Common/utils'
import { VideoPlayer as VideoPlayerCommon } from '../../../Common/VideoPlayer'
import type { Content, ProfilePreview } from '../../../types'

import styles from './Post.module.css'

import type { JSX } from 'react'

const getText = (
  text: string | undefined,
): ReturnType<typeof parseTextToElements> | undefined => {
  const maxLength = getPreviewCharLimit(SERVICE_TIKTOK)

  const truncatedText = truncateText(text, maxLength)
  if (truncatedText) {
    return parseTextToElements(
      truncatedText,
      parseTikTokLinks(truncatedText),
      (element, key) => (
        <span
          className={styles.hashtagText}
          key={key}
          data-testid="tiktok-preview-hashtag"
        >
          {element.displayString}
        </span>
      ),
    )
  }
}

const Ellipse = ({
  selected = false,
  size = 'medium',
}: {
  selected?: boolean
  size?: string
}): JSX.Element =>
  selected ? (
    <div className={styles.ellipseSelected} data-testid="ellipse" />
  ) : (
    <div
      className={clsx(
        styles.ellipse,
        size === 'small' && styles.ellipseSmall,
        size === 'smaller' && styles.ellipseSmaller,
      )}
      data-testid="ellipse"
    />
  )

const Ellipses = ({
  visible,
  totalImages,
}: {
  visible: boolean
  totalImages: number
}): JSX.Element => {
  const components: JSX.Element[] = []
  for (let i = 1; i < totalImages; i += 1) {
    components.push(<Ellipse />)
  }
  return (
    <div
      key="ellipses"
      data-testid="ellipses"
      className={clsx(
        styles.ellipsesWrapper,
        !visible && styles.ellipsesHidden,
      )}
    >
      <Ellipse selected />
      {components}
    </div>
  )
}

export const TikTokPost = ({
  content,
  profile,
}: {
  content: Content
  profile: ProfilePreview
}): JSX.Element => {
  const { avatar, username } = profile

  const { text, images, video, isAiGenerated } = content

  const maxLength = getPreviewCharLimit(SERVICE_TIKTOK)
  const textToDisplay = getText(text)

  const { hasImages, hasVideo } = getMediaProperties(content, null)

  const videoOrientationClass =
    video && getOrientation(video) === ImageOrientation.Portrait
      ? styles.videoPortrait
      : styles.videoNonPortrait

  return (
    <PostWrapperCommon className={styles.postWrapper} data-testid="tiktok-post">
      {hasImages && images && images[0]?.url && (
        <div className={styles.imageWrapper}>
          <img
            className={styles.image}
            src={images[0].url}
            alt="TikTok post media"
          />
        </div>
      )}

      {hasVideo && video && (
        <VideoPlayerCommon
          video={video}
          className={clsx(styles.videoPlayer, videoOrientationClass)}
        />
      )}
      {hasImages && images && images.length >= 2 && (
        <div className={styles.ellipsesWrapper}>
          <Ellipses totalImages={images.length} visible={true} />
        </div>
      )}
      {!hasImages && !hasVideo && (
        <EmptyMediaPlaceholder service="tiktok" className={styles.empty} />
      )}
      <div className={styles.metadataWrapper}>
        {isAiGenerated && (
          <PreviewAiMarker
            label="Contains AI-generated media"
            className={styles.aiMarker}
          />
        )}
        {!hasImages && (
          <CaptionsIcon size={18} className={styles.captionIcon} />
        )}

        <div className={styles.usernameWrapper}>
          <span className={styles.username}>
            <span translate="no">{username}</span>{' '}
            {hasImages && (
              <span className={styles.iconImageWrapper}>
                <ImageIcon size={18} className={styles.photoIcon} />{' '}
                <span className={styles.photoLabel}>Photo</span>
              </span>
            )}
          </span>
        </div>
        {text && (
          <div className={styles.caption}>
            <span translate="no">{textToDisplay}</span>
            {text && textExceedsCharLimit(text, maxLength) && (
              <span className={styles.seeMore}>... more</span>
            )}
          </div>
        )}

        <div className={styles.extraData}>
          {!hasImages && (
            <div className={styles.audio}>
              <MusicIcon size={12} color="white" />
              <span className={styles.wrappedText}>
                original sound - <span translate="no">{username}</span>
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.topBarText}>
        <span className={styles.followingText}>Following</span>
        <span className={styles.forYouText}>For You</span>
        <div className={styles.selectedTextBar} />
      </div>
      <div className={styles.searchIconWrapper}>
        <SearchIcon size={20} color="white" />
      </div>
      <div className={styles.iconsSideSection}>
        <TikTokAvatar avatar={avatar} />
        <HeartIcon size={24} color="white" />
        <CommentIcon size={24} color="white" />
        <BookmarkIcon size={24} color="white" />
        <ShareIcon size={24} color="white" />
        <div className={styles.audioAvatarWrapper}>
          <div className={styles.audioAvatarBackground} />
          <div
            className={styles.audioAvatar}
            data-testid="avatar"
            style={{
              backgroundImage: `url(${avatar}), url(${DEFAULT_AVATAR})`,
            }}
          />
        </div>
      </div>
    </PostWrapperCommon>
  )
}
